'use client';

import { cn } from '@kit/ui/utils';

import type { Chapter45Anchor, Chapter45Block } from '../../chapter45';
import { HighlightedText } from './highlighted-text';

interface ContentBlockProps {
  block: Chapter45Block;
  anchorsByBlockId: Map<string, Chapter45Anchor[]>;
  firstAnchors: Set<string>;
  lastAnchors: Set<string>;
  prevBlockType?: Chapter45Block['type'];
}

export function ContentBlock({
  block,
  anchorsByBlockId,
  firstAnchors,
  lastAnchors,
  prevBlockType,
}: ContentBlockProps) {
  switch (block.type) {
    case 'hr':
      return (
        <hr
          data-ch45-blockid={block.blockId}
          className="my-4 border-zinc-200/50 dark:border-zinc-700/50"
        />
      );

    case 'paragraph': {
      const followsList = prevBlockType === 'list';
      return (
        <p
          data-ch45-blockid={block.blockId}
          className={cn(
            'text-[13px] leading-[1.7] text-zinc-600 dark:text-zinc-400',
            followsList ? 'mt-1 mb-2.5' : 'my-2.5',
          )}
        >
          <HighlightedText
            text={block.text}
            ranges={anchorsByBlockId.get(block.blockId)}
            firstAnchors={firstAnchors}
            lastAnchors={lastAnchors}
          />
        </p>
      );
    }

    case 'list':
      return (
        <ul data-ch45-blockid={block.blockId} className="my-3 space-y-3 pl-4">
          {block.items.map((item) => (
            <ListItem
              key={item.blockId}
              item={item}
              anchorsByBlockId={anchorsByBlockId}
              firstAnchors={firstAnchors}
              lastAnchors={lastAnchors}
            />
          ))}
        </ul>
      );

    case 'table':
      return (
        <TableBlock
          block={block}
          anchorsByBlockId={anchorsByBlockId}
          firstAnchors={firstAnchors}
          lastAnchors={lastAnchors}
        />
      );

    case 'html-block':
      return (
        <HtmlBlock
          block={block}
          anchorsByBlockId={anchorsByBlockId}
          firstAnchors={firstAnchors}
          lastAnchors={lastAnchors}
        />
      );

    default:
      return null;
  }
}

// ============================================================================
// List Item Component
// ============================================================================

interface ListItemProps {
  item: { blockId: string; indent: boolean; text: string };
  anchorsByBlockId: Map<string, Chapter45Anchor[]>;
  firstAnchors: Set<string>;
  lastAnchors: Set<string>;
}

function ListItem({
  item,
  anchorsByBlockId,
  firstAnchors,
  lastAnchors,
}: ListItemProps) {
  const lines = item.text.split('\n');
  const hasMultipleLines = lines.length > 1;
  const firstLine = lines[0] ?? '';
  const restLines = lines.slice(1).join('\n');

  const isTitlePattern =
    hasMultipleLines &&
    firstLine.length < 80 &&
    (firstLine.startsWith('*') || /^[A-ZĄĆĘŁŃÓŚŹŻ]/.test(firstLine));

  if (isTitlePattern) {
    const itemAnchors = anchorsByBlockId.get(item.blockId);
    const firstLineLength = firstLine.length;
    const splitOffset = firstLineLength + 1;

    const firstLineAnchors = itemAnchors
      ?.filter((a) => a.start < firstLineLength)
      .map((a) => ({
        ...a,
        start: a.start,
        end: Math.min(a.end, firstLineLength),
      }));

    const restLineAnchors = itemAnchors
      ?.filter((a) => a.end > splitOffset)
      .map((a) => ({
        ...a,
        start: Math.max(0, a.start - splitOffset),
        end: a.end - splitOffset,
      }));

    return (
      <li
        data-ch45-blockid={item.blockId}
        className={cn(
          'relative text-[13px] leading-[1.6] text-zinc-600 dark:text-zinc-400',
          'before:absolute before:top-[0.6em] before:-left-3 before:h-1 before:w-1 before:rounded-full before:bg-current before:opacity-40',
          item.indent && 'ml-4',
        )}
      >
        <div className="font-medium text-zinc-700 dark:text-zinc-300">
          <HighlightedText
            text={firstLine}
            ranges={firstLineAnchors}
            firstAnchors={firstAnchors}
            lastAnchors={lastAnchors}
          />
        </div>
        <div className="mt-1 pl-1 text-zinc-600 dark:text-zinc-400">
          <HighlightedText
            text={restLines}
            ranges={restLineAnchors}
            firstAnchors={firstAnchors}
            lastAnchors={lastAnchors}
          />
        </div>
      </li>
    );
  }

  return (
    <li
      data-ch45-blockid={item.blockId}
      className={cn(
        'relative text-[13px] leading-[1.6] text-zinc-600 dark:text-zinc-400',
        'before:absolute before:top-[0.6em] before:-left-3 before:h-1 before:w-1 before:rounded-full before:bg-current before:opacity-40',
        item.indent && 'ml-4',
      )}
    >
      <HighlightedText
        text={item.text}
        ranges={anchorsByBlockId.get(item.blockId)}
        firstAnchors={firstAnchors}
        lastAnchors={lastAnchors}
      />
    </li>
  );
}

// ============================================================================
// Table Block Component
// ============================================================================

interface TableBlockProps {
  block: Extract<Chapter45Block, { type: 'table' }>;
  anchorsByBlockId: Map<string, Chapter45Anchor[]>;
  firstAnchors: Set<string>;
  lastAnchors: Set<string>;
}

function TableBlock({
  block,
  anchorsByBlockId,
  firstAnchors,
  lastAnchors,
}: TableBlockProps) {
  const { columnWidths, headerRowCount } = calculateTableLayout(block);

  const headerRows = block.rows.slice(0, headerRowCount);
  const bodyRows = block.rows.slice(headerRowCount);

  return (
    <div
      data-ch45-blockid={block.blockId}
      className="my-4 overflow-x-auto rounded-lg border border-zinc-300 dark:border-zinc-600"
    >
      <table className="w-full border-collapse text-[13px]">
        <colgroup>
          {columnWidths.map((width, i) => (
            <col key={i} style={{ width, minWidth: '80px' }} />
          ))}
        </colgroup>
        {headerRowCount > 0 && (
          <thead>
            {headerRows.map((row, r) => (
              <tr
                key={`${block.blockId}:header:${r}`}
                className="bg-zinc-100 dark:bg-zinc-800"
              >
                {row.map((cell, c) => {
                  const cellId = block.cellBlockIds[r]?.[c];
                  return (
                    <th
                      key={cellId ?? c}
                      data-ch45-blockid={cellId ?? undefined}
                      rowSpan={cell.rowspan}
                      colSpan={cell.colspan}
                      className="border border-zinc-300 px-3 py-2.5 text-left align-top text-xs font-semibold tracking-wide text-zinc-600 uppercase dark:border-zinc-600 dark:text-zinc-300"
                    >
                      <HighlightedText
                        text={cell.content}
                        ranges={
                          cellId ? anchorsByBlockId.get(cellId) : undefined
                        }
                        firstAnchors={firstAnchors}
                        lastAnchors={lastAnchors}
                      />
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
        )}
        {bodyRows.length > 0 && (
          <tbody>
            {bodyRows.map((row, r) => {
              const actualRowIndex = headerRowCount + r;
              return (
                <tr
                  key={`${block.blockId}:row:${r}`}
                  className="transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                >
                  {row.map((cell, c) => {
                    const cellId = block.cellBlockIds[actualRowIndex]?.[c];
                    const CellTag = cell.isHeader ? 'th' : 'td';
                    const cellClassName = cell.isHeader
                      ? 'border border-zinc-300 px-3 py-2.5 text-left align-top text-xs font-semibold tracking-wide text-zinc-600 uppercase dark:border-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800'
                      : 'border border-zinc-200 px-3 py-2.5 align-top text-zinc-600 dark:border-zinc-700 dark:text-zinc-400';
                    return (
                      <CellTag
                        key={cellId ?? c}
                        data-ch45-blockid={cellId ?? undefined}
                        rowSpan={cell.rowspan}
                        colSpan={cell.colspan}
                        className={cellClassName}
                      >
                        <HighlightedText
                          text={cell.content}
                          ranges={
                            cellId ? anchorsByBlockId.get(cellId) : undefined
                          }
                          firstAnchors={firstAnchors}
                          lastAnchors={lastAnchors}
                        />
                      </CellTag>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
    </div>
  );
}

// ============================================================================
// HTML Block Component (Table + Legend)
// ============================================================================

interface HtmlBlockProps {
  block: Extract<Chapter45Block, { type: 'html-block' }>;
  anchorsByBlockId: Map<string, Chapter45Anchor[]>;
  firstAnchors: Set<string>;
  lastAnchors: Set<string>;
}

function HtmlBlock({
  block,
  anchorsByBlockId,
  firstAnchors,
  lastAnchors,
}: HtmlBlockProps) {
  const { columnWidths, headerRowCount } = calculateTableLayout(block);

  const headerRows = block.rows.slice(0, headerRowCount);
  const bodyRows = block.rows.slice(headerRowCount);

  return (
    <div data-ch45-blockid={block.blockId}>
      <div className="my-4 overflow-x-auto rounded-lg border border-zinc-300 dark:border-zinc-600">
        <table className="w-full border-collapse text-[13px]">
          <colgroup>
            {columnWidths.map((width, i) => (
              <col key={i} style={{ width, minWidth: '80px' }} />
            ))}
          </colgroup>
          {headerRowCount > 0 && (
            <thead>
              {headerRows.map((row, r) => (
                <tr
                  key={`${block.blockId}:header:${r}`}
                  className="bg-zinc-100 dark:bg-zinc-800"
                >
                  {row.map((cell, c) => {
                    const cellId = block.cellBlockIds[r]?.[c];
                    return (
                      <th
                        key={cellId ?? c}
                        data-ch45-blockid={cellId ?? undefined}
                        rowSpan={cell.rowspan}
                        colSpan={cell.colspan}
                        className="border border-zinc-300 px-3 py-2.5 text-left align-top text-xs font-semibold tracking-wide text-zinc-600 uppercase dark:border-zinc-600 dark:text-zinc-300"
                      >
                        <HighlightedText
                          text={cell.content}
                          ranges={
                            cellId ? anchorsByBlockId.get(cellId) : undefined
                          }
                          firstAnchors={firstAnchors}
                          lastAnchors={lastAnchors}
                        />
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
          )}
          {bodyRows.length > 0 && (
            <tbody>
              {bodyRows.map((row, r) => {
                const actualRowIndex = headerRowCount + r;
                return (
                  <tr
                    key={`${block.blockId}:row:${r}`}
                    className="transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                  >
                    {row.map((cell, c) => {
                      const cellId = block.cellBlockIds[actualRowIndex]?.[c];
                      const CellTag = cell.isHeader ? 'th' : 'td';
                      const cellClassName = cell.isHeader
                        ? 'border border-zinc-300 px-3 py-2.5 text-left align-top text-xs font-semibold tracking-wide text-zinc-600 uppercase dark:border-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800'
                        : 'border border-zinc-200 px-3 py-2.5 align-top text-zinc-600 dark:border-zinc-700 dark:text-zinc-400';
                      return (
                        <CellTag
                          key={cellId ?? c}
                          data-ch45-blockid={cellId ?? undefined}
                          rowSpan={cell.rowspan}
                          colSpan={cell.colspan}
                          className={cellClassName}
                        >
                          <HighlightedText
                            text={cell.content}
                            ranges={
                              cellId ? anchorsByBlockId.get(cellId) : undefined
                            }
                            firstAnchors={firstAnchors}
                            lastAnchors={lastAnchors}
                          />
                        </CellTag>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>

      {block.legendParagraphs.length > 0 && (
        <div className="mt-2 mb-4 space-y-1 text-[12px] text-zinc-500 dark:text-zinc-400">
          {block.legendParagraphs.map((para) => (
            <p
              key={para.blockId}
              data-ch45-blockid={para.blockId}
              className="leading-relaxed"
            >
              <HighlightedText
                text={para.text}
                ranges={anchorsByBlockId.get(para.blockId)}
                firstAnchors={firstAnchors}
                lastAnchors={lastAnchors}
              />
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Table Layout Calculation
// ============================================================================

interface TableLayout {
  columnCount: number;
  columnWidths: string[];
  headerRowCount: number;
}

function calculateTableLayout(
  block:
    | Extract<Chapter45Block, { type: 'table' }>
    | Extract<Chapter45Block, { type: 'html-block' }>,
): TableLayout {
  const firstRow = block.rows[0] ?? [];
  let columnCount = 0;
  for (const cell of firstRow) {
    columnCount += cell.colspan ?? 1;
  }

  const columnMaxLengths: number[] = Array(columnCount).fill(0);
  const sampleRows = block.rows.slice(0, 10);

  for (const row of sampleRows) {
    let logicalCol = 0;
    for (const cell of row) {
      const colSpan = cell.colspan ?? 1;
      const contentLen = cell.content.length;
      const lenPerCol = Math.ceil(contentLen / colSpan);
      for (let i = 0; i < colSpan && logicalCol + i < columnCount; i++) {
        columnMaxLengths[logicalCol + i] = Math.max(
          columnMaxLengths[logicalCol + i] ?? 0,
          lenPerCol,
        );
      }
      logicalCol += colSpan;
    }
  }

  const totalLength = columnMaxLengths.reduce((a, b) => a + b, 0) || 1;
  const columnWidths = columnMaxLengths.map((len) => {
    const percentage = Math.max(15, Math.min(50, (len / totalLength) * 100));
    return `${percentage}%`;
  });

  let headerRowCount = 0;
  for (const row of block.rows) {
    if (row.every((cell) => cell.isHeader)) {
      headerRowCount++;
    } else {
      break;
    }
  }

  return { columnCount, columnWidths, headerRowCount };
}
